import { useState } from 'react'
import { Gauge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGtmUsageOverviewQuery } from './gtm-workspace-queries'
import { GtmTopUpDialog } from './billing/gtm-top-up-dialog'
import { Button } from '@/components/ui/button'
import {
  formatRunOutDate,
  formatUsdCents,
} from '@/features/billing/domain/credit-balance'
import { SidebarTooltip } from '@/components/molecules/sidebar/sidebar-tooltip'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { cn } from '@/shared/lib/utils'

/** At or below this share of capacity left, the figures turn red. */
const LOW_SHARE = 0.1

/**
 * The organization's email capacity in the rolling 24 hours (the bar is
 * what was used, the line what is left), when warm-up raises it next, and its credit
 * balance with the forecast run-out date and a top-up, above the account
 * controls. Figures come from the server only: while loading nothing is
 * shown as zero, and a failed load says so.
 */
export function GtmSidebarUsage({
  workspaceId,
  collapsed,
}: {
  workspaceId: string
  collapsed: boolean
}) {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const query = useGtmUsageOverviewQuery(workspaceId)
  const [topUpOpen, setTopUpOpen] = useState(false)
  const usage = query.data
  const failed = query.isError && !usage

  const capacity = usage?.capacity.state === 'ready' ? usage.capacity : null
  const capacityText = !usage
    ? null
    : capacity
      ? t('ws_usageCapacityValue', {
          remaining: capacity.remaining,
          total: capacity.total,
        })
      : t('ws_usageNotAvailable')
  const balance = usage?.balance ?? null
  const balanceText = balance
    ? formatUsdCents(balance.balanceCents, lang)
    : null
  const runOut = balance ? formatRunOutDate(balance.runOutAt, lang) : null
  const balanceLow = balance !== null && balance.status !== 'ok'
  const share =
    capacity && capacity.total > 0 ? capacity.remaining / capacity.total : null
  const low = share !== null && share <= LOW_SHARE
  const usedShare =
    capacity && capacity.total > 0 ? capacity.used / capacity.total : 0
  const nextLimit = capacity?.nextLimit ?? null
  const nextLimitText = nextLimit
    ? t('ws_usageNextLimit', {
        limit: nextLimit.total,
        date: formatRunOutDate(nextLimit.at, lang),
      })
    : null

  if (collapsed) {
    const label = failed
      ? t('ws_usageLoadFailed')
      : capacityText && balanceText
        ? `${t('ws_usageCapacity')}: ${capacityText} · ${t('ws_usageBalance')}: ${balanceText}`
        : t('ws_sidebarUsageTitle')
    return (
      <SidebarTooltip label={label}>
        <span
          role="img"
          aria-label={label}
          className="flex size-9 items-center justify-center rounded-md text-sidebar-foreground/70"
        >
          <Gauge className="size-4" aria-hidden="true" />
        </span>
      </SidebarTooltip>
    )
  }

  return (
    <section
      aria-label={t('ws_sidebarUsageTitle')}
      aria-busy={query.isPending}
      className="space-y-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 p-3 text-xs"
    >
      {failed ? (
        <p role="alert" className="text-sidebar-foreground/70">
          {t('ws_usageLoadFailed')}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sidebar-foreground">
                {t('ws_usageCapacity')}
              </span>
              {capacityText === null ? <Skeleton className="w-12" /> : null}
            </div>
            {capacity ? (
              <div
                role="progressbar"
                aria-label={t('ws_usageCapacityUsed')}
                aria-valuemin={0}
                aria-valuemax={capacity.total}
                aria-valuenow={capacity.used}
                className="h-2 w-full overflow-hidden rounded-full bg-sidebar-border/60"
              >
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    low ? 'bg-destructive' : 'bg-upgrade-cta',
                  )}
                  style={{ width: `${Math.round(usedShare * 100)}%` }}
                />
              </div>
            ) : capacityText === null ? (
              <div className="h-2 w-full animate-pulse rounded-full bg-sidebar-border/60" />
            ) : null}
            {capacityText !== null ? (
              <p
                className={cn(
                  capacity
                    ? low
                      ? 'text-destructive'
                      : 'text-sidebar-foreground/70'
                    : 'text-sidebar-foreground/60',
                )}
              >
                {capacityText}
              </p>
            ) : null}
            {nextLimitText ? (
              <p className="text-sidebar-foreground/60">{nextLimitText}</p>
            ) : null}
          </div>
          <div className="space-y-2 border-t border-sidebar-border/70 pt-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sidebar-foreground">
                {t('ws_usageBalance')}
              </span>
              {balanceText === null ? (
                <Skeleton className="w-14" />
              ) : (
                <span
                  className={cn(
                    'font-medium tabular-nums',
                    balanceLow
                      ? 'text-destructive'
                      : 'text-sidebar-foreground/70',
                  )}
                >
                  {balanceText}
                </span>
              )}
            </div>
            {runOut ? (
              <p className="tabular-nums text-sidebar-foreground/60">
                {t('ws_balanceRunsOut', { date: runOut })}
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 w-full"
              disabled={!balance}
              onClick={() => setTopUpOpen(true)}
            >
              {t('ws_topUp')}
            </Button>
          </div>
        </>
      )}
      <GtmTopUpDialog
        workspaceId={workspaceId}
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
      />
    </section>
  )
}

function Skeleton({ className }: { className: string }) {
  return (
    <span
      className={cn(
        'inline-block h-3 animate-pulse rounded bg-sidebar-border/70',
        className,
      )}
    />
  )
}
